import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { PriceRange } from "@is/types";
import { positionToPrice, priceToPosition } from "@is/utils";

type Props = {
  minValue: number;
  maxValue: number;
  value: PriceRange;
  onChange: (range: PriceRange) => void;
  onRelease?: (range: PriceRange) => void;
};

// rc-slider runs in a linear 0–100 position space; the logarithmic mapping to
// actual prices happens here at the boundary (see positionToPrice). 0.1 steps
// give smooth dragging across the whole range.
export const POSITION_MIN = 0;
export const POSITION_MAX = 100;
export const POSITION_STEP = 0.1;

// Minimum on-screen distance (px) between the two price labels before they get
// pushed apart so they never overlap.
const LABEL_GAP_PX = 96;

const clampPercent = (percent: number) => Math.min(100, Math.max(0, percent));

const toPositions = (
  value: PriceRange,
  minValue: number,
  maxValue: number
): [number, number] => [
  priceToPosition(value.min ?? minValue, maxValue),
  priceToPosition(value.max ?? maxValue, maxValue),
];

// Spreads two near/overlapping positions to at least `gapPercent` apart, centred
// on their midpoint and clamped to the [0, 100] track. Used only for the labels.
const spreadPositions = (
  lowPercent: number,
  highPercent: number,
  gapPercent: number
): [number, number] => {
  if (gapPercent <= 0 || highPercent - lowPercent >= gapPercent) {
    return [lowPercent, highPercent];
  }

  const mid = (lowPercent + highPercent) / 2;
  let low = mid - gapPercent / 2;
  let high = mid + gapPercent / 2;

  if (low < 0) {
    low = 0;
    high = gapPercent;
  } else if (high > 100) {
    high = 100;
    low = 100 - gapPercent;
  }

  return [low, high];
};

const useManagePriceRangeSlider = ({
  minValue,
  maxValue,
  value,
  onChange,
  onRelease,
}: Props) => {
  // Position state is the source of truth while dragging — kept lossless so the
  // handle never jitters from the price-rounding round-trip.
  const [positions, setPositions] = useState<[number, number]>(() =>
    toPositions(value, minValue, maxValue)
  );
  const [trackWidth, setTrackWidth] = useState(0);

  const sliderContainerRef = useRef<HTMLDivElement>(null);
  const lastEmittedRef = useRef<PriceRange>(value);

  // Resync from props only on external changes (e.g. Clear All), never from our
  // own emits — otherwise the rounded value we just sent would reset the drag.
  useEffect(() => {
    if (
      value.min !== lastEmittedRef.current.min ||
      value.max !== lastEmittedRef.current.max
    ) {
      setPositions(toPositions(value, minValue, maxValue));
      lastEmittedRef.current = value;
    }
  }, [value.min, value.max, minValue, maxValue]);

  // Track width drives the label nudging.
  useEffect(() => {
    const element = sliderContainerRef.current;
    if (!element || typeof ResizeObserver === "undefined") {
      if (element) setTrackWidth(element.getBoundingClientRect().width);
      return;
    }

    const update = () => setTrackWidth(element.getBoundingClientRect().width);
    update();

    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const [minPercent, maxPercent] = positions;

  const localMin = useMemo(
    () => positionToPrice(minPercent, maxValue),
    [minPercent, maxValue]
  );
  const localMax = useMemo(
    () => positionToPrice(maxPercent, maxValue),
    [maxPercent, maxValue]
  );

  const emit = useCallback(
    (next: [number, number], release: boolean) => {
      const prices: PriceRange = {
        min: positionToPrice(next[0], maxValue),
        max: positionToPrice(next[1], maxValue),
      };
      lastEmittedRef.current = prices;

      if (release) {
        onRelease?.(prices);
      } else {
        onChange(prices);
      }
    },
    [maxValue, onChange, onRelease]
  );

  const handleSliderChange = useCallback(
    (sliderValue: number | number[]) => {
      if (!Array.isArray(sliderValue)) return;

      const next: [number, number] = [sliderValue[0], sliderValue[1]];
      setPositions(next);
      emit(next, false);
    },
    [emit]
  );

  const handleSliderComplete = useCallback(
    (sliderValue: number | number[]) => {
      if (!Array.isArray(sliderValue)) return;

      emit([sliderValue[0], sliderValue[1]], true);
    },
    [emit]
  );

  const labelGapPercent = useMemo(
    () => (trackWidth > 0 ? (LABEL_GAP_PX / trackWidth) * 100 : 0),
    [trackWidth]
  );

  const [minLabelPercent, maxLabelPercent] = useMemo(
    () => spreadPositions(minPercent, maxPercent, labelGapPercent),
    [minPercent, maxPercent, labelGapPercent]
  );

  // translateX keeps each label within the track bounds: left-aligned at 0%,
  // right-aligned at 100%, interpolated in between.
  const minLabelTranslateX = useMemo(
    () => -clampPercent(minLabelPercent),
    [minLabelPercent]
  );
  const maxLabelTranslateX = useMemo(
    () => -clampPercent(maxLabelPercent),
    [maxLabelPercent]
  );

  const formatPrice = useCallback(
    (price: number) => Math.round(price).toString(),
    []
  );

  return {
    sliderValue: positions,
    sliderMin: POSITION_MIN,
    sliderMax: POSITION_MAX,
    sliderStep: POSITION_STEP,
    handleSliderChange,
    handleSliderComplete,
    localMin,
    localMax,
    minLabelPercent,
    maxLabelPercent,
    minLabelTranslateX,
    maxLabelTranslateX,
    sliderContainerRef,
    formatMinPrice: formatPrice,
    formatMaxPrice: formatPrice,
  };
};

export default useManagePriceRangeSlider;
